import L from "leaflet";
import type { Polygon, Position } from "geojson";
import { MID_ICON, SNAP_PX, VTX_ICON } from "./AoiSnapEditEngine";

/** Una operación sobre un vértice, lista para mandar al servidor.
 *
 *  Las tres formas se distinguen por los campos, igual que el request:
 *   - **mover**: `origen` y `destino`;
 *   - **quitar**: sólo `origen`;
 *   - **insertar**: `insercion` y `destino` — una sola operación, porque así la
 *     vive el usuario: agarra el punto medio de un tramo y lo arrastra. */
export type OperacionVertice = {
  origen?: Position;
  destino?: Position;
  /** `loteId` es el id LOCAL del modelo. Lo traduce al uuid del registro
   *  temporal `LoteSplitMapRender`, que es el que tiene el estado a mano. */
  insercion?: { loteId: number; indiceArista: number };
};

/** La misma operación, con el lote nombrado como lo nombra el servidor. Es lo
 *  que viaja en el request. */
export type OperacionVerticeServidor = Omit<OperacionVertice, "insercion"> & {
  insercion?: { loteId: string; indiceArista: number };
};

/** Modo **Editar** del paso 4: los vértices del lote que el usuario eligió.
 *
 *  **Qué hace este motor y qué no.** Pone UN handle en pantalla —el del punto
 *  enganchado, de los tres candidatos del tramo bajo el cursor— y, al soltarlo,
 *  avisa qué operación hay que hacer. Nada más. No mide, no valida y no toca la
 *  geometría de ningún lote.
 *
 *  **La edición la resuelve el servidor** (`EditarVerticeLote`), y eso incluye
 *  lo topológico: mover un vértice lo mueve en todos los lotes que lo comparten
 *  y quitarlo lo quita de todos. Antes eso lo hacía este motor contra
 *  `LoteSplitState` con turf, y el servidor recibía la geometría ya cambiada sin
 *  forma de verificarla — el mismo problema que tenían el corte y la unión.
 *
 *  **La metodología es la del paso 3** (`AoiSnapEditEngine`): los vértices en
 *  reposo los dibuja GeoServer por WMS, el handle es verde y 2 px más grande que
 *  ellos —esa diferencia es el enganche—, y lo único que Leaflet pone en el mapa
 *  es ese handle. Antes había dos marcadores por vértice del lote, o sea un
 *  `<div>` del DOM por cada uno que Leaflet reposiciona en cada movimiento del
 *  mapa.
 *
 *  **El lote se elige con un CLICK**, no pasando el cursor por encima, y su
 *  geometría se lee de la base por gRPC. Con hover, acercarse al borde para
 *  agarrar un vértice significaba salirse del polígono, de ahí toda una
 *  maquinaria de salida diferida con temporizador para que el vértice no
 *  desapareciera justo en ese momento. Con un lote elegido explícitamente el
 *  handle vive mientras el usuario no cambie de lote.
 *
 *  **Qué no corre mientras se arrastra**: nada. Leaflet mueve el handle y se
 *  acabó; el borde salta a su lugar cuando la operación vuelve del servidor.
 *  Igual que en el paso 3.
 */
export class LoteVertexEditEngine {
  /** @param _onOperacion Hay una operación de vértice que mandar al servidor.
   *   La atiende quien tiene el registro y el servicio gRPC
   *   (`RegistroFincaPaso4Render`); este motor no habla con el backend. */
  constructor(
    private readonly _map: L.Map,
    private readonly _onOperacion: (operacion: OperacionVertice) => void,
  ) {}

  /** ¿Hay un arrastre en curso? Lo consulta `LoteSplitMapRender` para no
   *  repintar en medio de un arrastre. */
  get arrastrando(): boolean {
    return this._arrastrando;
  }

  /** El lote que se está editando, o `null`. */
  get loteSeleccionado(): number | null {
    return this._loteId;
  }

  activar(): void {
    if (this._enganchado) return;
    this._map.on("mousemove", this._alMoverElCursor);
    this._map.on("zoomend moveend", this._alCambiarLaVista);
    this._map.getContainer().addEventListener("mouseleave", this._alSalir);
    this._enganchado = true;
  }

  desactivar(): void {
    this.soltar();
    if (!this._enganchado) return;
    this._map.off("mousemove", this._alMoverElCursor);
    this._map.off("zoomend moveend", this._alCambiarLaVista);
    this._map.getContainer().removeEventListener("mouseleave", this._alSalir);
    this._enganchado = false;
  }

  /** Pone en edición el lote que el usuario clickeó. La geometría llega de
   *  afuera —la lee el llamador por gRPC, de la base— y este motor no consulta
   *  servicios, igual que `AoiSnapEditEngine`. */
  seleccionar(loteId: number, poligono: Polygon): void {
    this._limpiarHandle();
    this._loteId = loteId;
    // Se descarta el punto de cierre de cada anillo: se manejan como cíclicos.
    this._anillos = poligono.coordinates.map((anillo) => {
      const puntos = anillo.slice();
      const n = puntos.length;
      if (
        n > 1 &&
        puntos[0][0] === puntos[n - 1][0] &&
        puntos[0][1] === puntos[n - 1][1]
      ) {
        puntos.pop();
      }
      return puntos;
    });
    this._proyectar();
  }

  /** Suelta el lote en edición. */
  soltar(): void {
    this._limpiarHandle();
    this._loteId = null;
    this._anillos = [];
    this._cache = [];
  }

  /** Vuelve a proyectar y rehace el handle con la geometría que tenga ahora el
   *  lote en edición.
   *
   *  Lo llama el mapa tras un cambio de estado — que en el paso 4 significa que
   *  una operación volvió del servidor y los lotes se recargaron. La geometría
   *  se toma de ahí y no de otra lectura de la base: el estado ACABA de salir de la
   *  respuesta del servidor, así que es la misma. */
  refrescar(poligono: Polygon | null, loteId: number | null = null): void {
    if (!this._enganchado || this._arrastrando) return;
    // `loteId` reengancha la edición a otro id local SIN soltarla. Hace falta
    // porque el id local de un lote no sobrevive a la respuesta del servidor:
    // `LoteSplitState.init()` se los estrena a todos (ver
    // `LoteSplitMapRender._tempIdEnEdicion`). Quien llama sabe cuál es el nuevo;
    // acá sólo se adopta.
    const id = loteId ?? this._loteId;
    if (id === null) return;
    if (!poligono) {
      // El lote dejó de existir (lo cortaron, lo unieron, se recargó): no hay
      // nada que editar.
      this.soltar();
      return;
    }
    this.seleccionar(id, poligono);
    this._resolver(true);
  }

  dispose(): void {
    this.desactivar();
  }

  // ---------------------------------------------------------------- estado
  private _enganchado = false;
  private _arrastrando = false;
  private _sobreHandle = false;
  private _rafPendiente = false;
  private _cursor: L.Point | null = null;
  private _loteId: number | null = null;
  private _anillos: Position[][] = [];
  private _cache: { ri: number; n: number; xs: Float64Array; ys: Float64Array }[] = [];
  /** El handle del punto enganchado, o nada. Es un arreglo y no un campo suelto
   *  porque el alta y la baja son el mismo código para el handle de un vértice y
   *  para el del punto medio. */
  private _handles: L.Marker[] = [];
  /** Qué punto está enganchado, para no tocar el DOM si no cambió. */
  private _hoverKey = "";

  // ------------------------------------------------------------ proyección
  /** Vértices a píxeles, una vez por vista. Es lo que permite resolver el
   *  enganche con aritmética y sin recorrer capas de Leaflet. */
  private _proyectar(): void {
    this._cache = this._anillos.map((anillo, ri) => {
      const n = anillo.length;
      const xs = new Float64Array(n);
      const ys = new Float64Array(n);
      for (let i = 0; i < n; i++) {
        const p = this._map.latLngToLayerPoint(
          L.latLng(anillo[i][1], anillo[i][0]),
        );
        xs[i] = p.x;
        ys[i] = p.y;
      }
      return { ri, n, xs, ys };
    });
  }

  /** Distancia² de un punto a un segmento. Al cuadrado para no sacar raíz
   *  dentro del bucle: sólo se comparan entre sí. */
  private static _dist2(
    px: number, py: number, ax: number, ay: number, bx: number, by: number,
  ): number {
    const dx = bx - ax;
    const dy = by - ay;
    const len2 = dx * dx + dy * dy;
    let t = 0;
    if (len2 > 0) t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / len2));
    const ex = px - (ax + t * dx);
    const ey = py - (ay + t * dy);
    return ex * ex + ey * ey;
  }

  // -------------------------------------------------------------- eventos
  private _alMoverElCursor = (ev: L.LeafletMouseEvent): void => {
    this._cursor = ev.layerPoint;
    // Se recalcula en rAF: el navegador dispara muchos más `mousemove` que
    // cuadros, y resolver el enganche una vez por cuadro alcanza.
    if (this._rafPendiente) return;
    this._rafPendiente = true;
    requestAnimationFrame(() => {
      this._rafPendiente = false;
      this._resolver(false);
    });
  };

  private _alCambiarLaVista = (): void => {
    if (this._loteId === null) return;
    this._proyectar();
    this._resolver(true);
  };

  /** `mouseleave` del contenedor y NO el `mouseout` del mapa: `mouseout`
   *  también salta al pasar sobre el handle, y lo borraría justo cuando el
   *  usuario va a agarrarlo. */
  private _alSalir = (): void => {
    this._cursor = null;
    if (!this._arrastrando) this._limpiarHandle();
  };

  // -------------------------------------------------------------- el handle
  private _resolver(forzar: boolean): void {
    if (this._loteId === null || this._arrastrando) return;
    // Con el cursor sobre el handle no se recalcula: podría quedar más cerca de
    // otro tramo y desaparecería al ir a tomarlo.
    if (this._sobreHandle && !forzar) return;
    if (!this._cursor) {
      if (this._hoverKey) this._limpiarHandle();
      return;
    }

    const tol2 = SNAP_PX * SNAP_PX;
    const mx = this._cursor.x;
    const my = this._cursor.y;
    let mejor: {
      ri: number; i: number; j: number;
      ax: number; ay: number; bx: number; by: number;
    } | null = null;
    let mejorD = tol2;

    for (const rc of this._cache) {
      for (let i = 0; i < rc.n; i++) {
        const j = (i + 1) % rc.n;
        const ax = rc.xs[i], ay = rc.ys[i], bx = rc.xs[j], by = rc.ys[j];
        // Descarte por caja antes de medir: es lo que deja el bucle barato con
        // un lote de muchos vértices.
        if (
          mx < Math.min(ax, bx) - SNAP_PX || mx > Math.max(ax, bx) + SNAP_PX ||
          my < Math.min(ay, by) - SNAP_PX || my > Math.max(ay, by) + SNAP_PX
        ) continue;
        const d = LoteVertexEditEngine._dist2(mx, my, ax, ay, bx, by);
        if (d < mejorD) {
          mejorD = d;
          mejor = { ri: rc.ri, i, j, ax, ay, bx, by };
        }
      }
    }

    if (!mejor) {
      if (this._hoverKey) this._limpiarHandle();
      return;
    }

    const candidato = LoteVertexEditEngine._masCercano(mx, my, mejor);
    const clave = `${mejor.ri}/${mejor.i}-${mejor.j}/${candidato}`;
    if (clave === this._hoverKey && !forzar) return; // nada cambió: no tocar el DOM
    this._hoverKey = clave;

    this._limpiarHandle(true);
    const anillo = this._anillos[mejor.ri];

    this._handles.push(
      candidato === "medio"
        // El punto medio sólo se puede insertar en el anillo EXTERIOR: es el
        // único que el request sabe nombrar (por índice de arista), y los huecos
        // de un lote no son algo que este paso ofrezca editar.
        ? this._handleMedio(mejor.ri, mejor.i, anillo[mejor.i], anillo[mejor.j])
        : this._handleVertice(anillo[candidato === "a" ? mejor.i : mejor.j]),
    );
  }

  /** De los tres puntos enganchables de un tramo —sus dos extremos y su punto
   *  medio—, el más cercano al cursor. */
  private static _masCercano(
    mx: number,
    my: number,
    t: { ax: number; ay: number; bx: number; by: number },
  ): "a" | "b" | "medio" {
    const d2 = (x: number, y: number): number =>
      (mx - x) * (mx - x) + (my - y) * (my - y);

    const dA = d2(t.ax, t.ay);
    const dB = d2(t.bx, t.by);
    const dM = d2((t.ax + t.bx) / 2, (t.ay + t.by) / 2);

    if (dA <= dB && dA <= dM) return "a";
    if (dB <= dM) return "b";
    return "medio";
  }

  private _limpiarHandle(conservarClave = false): void {
    this._handles.forEach((m) => this._map.removeLayer(m));
    this._handles = [];
    this._sobreHandle = false;
    if (!conservarClave) this._hoverKey = "";
  }

  /** Engancha al handle lo que necesita para no pelearse con el mapa.
   *
   *  `mousedown` y `mouseup` NO pueden llegar al mapa: el click del mapa elige
   *  el lote a editar, así que sin esto agarrar un vértice cambiaría la
   *  selección. Un marcador de Leaflet sólo "captura" los tipos de evento que
   *  escucha — es el mismo detalle que documenta `AoiSnapEditEngine`. */
  private _conectar(m: L.Marker): void {
    m.on("mouseover", () => { this._sobreHandle = true; });
    m.on("mouseout", () => { this._sobreHandle = false; });
    m.on("mousedown", (ev: L.LeafletMouseEvent) => L.DomEvent.stop(ev));
    m.on("mouseup", (ev: L.LeafletMouseEvent) => L.DomEvent.stop(ev));
  }

  private _handleVertice(vertice: Position): L.Marker {
    const origen: Position = [vertice[0], vertice[1]];
    const m = L.marker(L.latLng(vertice[1], vertice[0]), {
      icon: VTX_ICON,
      draggable: true,
      keyboard: false,
      zIndexOffset: 1000,
      title: "Arrastrar = mover · click = eliminar",
    }).addTo(this._map);

    m.on("dragstart", () => { this._arrastrando = true; });
    m.on("dragend", () => {
      this._arrastrando = false;
      const destino = m.getLatLng();
      this._onOperacion({ origen, destino: [destino.lng, destino.lat] });
    });

    // Click = quitar el vértice, igual que el modo Editar del paso 3. Leaflet no
    // dispara este `click` tras un arrastre, así que soltar el handle no borra
    // el vértice.
    m.on("click", (ev: L.LeafletMouseEvent) => {
      L.DomEvent.stop(ev);
      this._onOperacion({ origen });
    });

    this._conectar(m);
    return m;
  }

  /** Handle "fantasma" en el medio del tramo. Al soltarlo se manda una sola
   *  operación: insertar ahí un vértice y moverlo a donde quedó. */
  private _handleMedio(
    ri: number,
    indiceArista: number,
    a: Position,
    b: Position,
  ): L.Marker {
    const medio = L.latLng((a[1] + b[1]) / 2, (a[0] + b[0]) / 2);
    const m = L.marker(medio, {
      icon: MID_ICON,
      draggable: true,
      keyboard: false,
      zIndexOffset: 900,
      title: "Arrastrar = agregar vértice",
    }).addTo(this._map);

    m.on("dragstart", () => { this._arrastrando = true; });
    m.on("dragend", () => {
      this._arrastrando = false;
      const loteId = this._loteId;
      const destino = m.getLatLng();
      // Sólo el anillo exterior: ver el comentario de `_resolver`.
      if (loteId === null || ri !== 0) {
        this._limpiarHandle();
        return;
      }
      this._onOperacion({
        insercion: { loteId, indiceArista },
        destino: [destino.lng, destino.lat],
      });
    });

    this._conectar(m);
    return m;
  }
}
